import React, { useEffect, useState } from "react";
import "./Refill.css";
import Header from "../../components/Header/header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import SizeCard from "./SizeCard";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";

/* ================================================================== */
/*  1. CONFIG (static data)                                           */
/* ================================================================== */

const API_URL = process.env.REACT_APP_API_LINK || "http://localhost:4100";
const PROGRESS = 75;

// Sizes ka order (cards isi order me dikhenge)
const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "2XL", "3XL", "4XL", "5XL"];

// "Choose a color" chips
const COLORS = [
    { id: "100", name: "Color 100", dot: "#378add", autoOrder: 23, soldOut: 1, validFrom: "26.01.26", validTo: "31.12.39" },
    { id: "277", name: "Color 277", dot: "#1d9e75", autoOrder: 0, soldOut: 0, validFrom: "26.01.27", validTo: "31.12.39" },
    { id: "290", name: "Color 290", dot: "#ba7517", autoOrder: 18, soldOut: 2, validFrom: "26.01.26", validTo: "31.12.39" },
];

// Card me + dabane par khulne wale fields
const EXTRA_FIELDS = [
    { key: "LstBst", label: "LstBst Menge" },
    { key: "Artikel", label: "Artikel" },
    { key: "Col", label: "Col" },
    { key: "Dim", label: "Dim" },
    { key: "Gro", label: "Last order date", de: "LstBst Datum" },
    { key: "RechImTag", label: "Rech. Im Tag" },
    { key: "A/ISts", label: "A/I Sts" },
    { key: "GultingVonDt", label: "Gültig Von Dt" },
    { key: "GultingBisDt", label: "Gültig Bis Dt" },
];

/* ================================================================== */
/*  2. HELPERS                                                        */
/* ================================================================== */

// 311239 -> "31.12.39"
const formatDate = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const s = String(v).padStart(6, "0");
    return `${s.slice(0, 2)}.${s.slice(2, 4)}.${s.slice(4, 6)}`;
};

// API ke keys -> SizeCard ke keys (mapping sirf yahan badalni hai)
const normalize = (r) => ({
    // main fields
    aoBisDatum: formatDate(r.DAORDT),
    lstBstDatum: formatDate(r.DINVD),
    newBest: r.DICLQ,
    abvekNach: r.DCCALI,
    offnAuf: r.DOPNO,
    grundBest: r.DMAXQ,
    kalkBest: r.DOCLQ,
    bedrfMangQt: r.DPROJ,
    // extra fields
    LstBst: r.DINLQT,
    Artikel: r.DSTY,
    Col: r.DCLR,
    Dim: r.DDIM,
    Gro: formatDate(r.DINVD),
    RechImTag: r.DINDQT,
    "A/ISts": r.DSTS,
    GultingVonDt: formatDate(r.DVAFR),
    GultingBisDt: formatDate(r.DVATO),
});

// Size ka rank (SIZE_ORDER me nahi hai to sabse last)
const sizeRank = (size) => {
    const i = SIZE_ORDER.indexOf(size);
    return i === -1 ? 999 : i;
};

/* ================================================================== */
/*  3. API HOOK                                                       */
/* ================================================================== */

const useRefillData = (enabled, soldTo, shipTo) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

useEffect(() => {
        if (!enabled || !soldTo || !shipTo) return;
 
        const url = `${API_URL}/api/v1/admin/getRefillData?SOL=${encodeURIComponent(soldTo)}&SHP=${encodeURIComponent(shipTo)}`;
 
        const load = async () => {
            try {
                setLoading(true);
                setError("");
 
                const res = await fetch(url);
                if (!res.ok) throw new Error(`Error ${res.status}`);
 
                const json = await res.json();
                setData(Array.isArray(json) ? json : json.data || json.result || json.results || []);
            } catch (e) {
                console.error("Refill API error:", e);
                setError("Unable to load data. Please try again later.");
                toast.error("Refill data load nahi ho paya", { toastId: "refill-load-error" });
            } finally {
                setLoading(false);
            }
        };
 
        load();
    }, [enabled, soldTo, shipTo]);
 
    return { data, loading, error };
};

/* ================================================================== */
/*  4. SMALL COMPONENTS                                               */
/* ================================================================== */

// Upar header ka ek input (label + input)
const HeaderField = ({ id, label, value, disabled = false }) => (
    <Col lg={2} className="refill_user_id_main">
        <div className="user_id_main_text">
            <Form.Label htmlFor={id} className="user_id_text p-0 ml-1 m-0">
                {label}
            </Form.Label>
        </div>
        <div>
            <Form.Control
                type="text"
                id={id}
                placeholder={`Enter ${label}`}
                className="refilling_input"
                value={value}
                disabled={disabled}
                readOnly={disabled}
            />
        </div>
    </Col>
);

// Ek color chip
const ColorChip = ({ color, active, onSelect }) => (
    <button
        type="button"
        className={`color_chip${active ? " color_chip_active" : ""}`}
        onClick={onSelect}
        aria-pressed={active}
    >
        <span className="color_chip_top">
            <span className="color_chip_dot" style={{ backgroundColor: color.dot }}></span>
            <span className="color_chip_name">{color.name}</span>
        </span>
        <span className="color_chip_meta">
            {color.autoOrder} auto-order
            {color.soldOut > 0 && (
                <>
                    {" · "}
                    <span className="color_chip_soldout">{color.soldOut} sold out</span>
                </>
            )}
        </span>
    </button>
);

/* ================================================================== */
/*  5. MAIN PAGE                                                      */
/* ================================================================== */

const Refill = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const isAllowed = location.state?.fromCustomerSummary === true;
    const selectedRow = location.state?.row;

    const [selectedColor, setSelectedColor] = useState("100");
    const [openSizes, setOpenSizes] = useState({}); // { M: true, L: false }

    const { data: refillData, loading, error } = useRefillData(
        isAllowed,
        selectedRow?.soldTo,
        selectedRow?.shipTo
    );

    // Direct URL se aaye to warning
    useEffect(() => {
        if (!isAllowed) {
            toast.error("You can't go directly to Refill. Please select a customer first", {
                toastId: "refill-direct-access",
            });
        }
    }, [isAllowed]);

    // ---- Data taiyar karo ----

    const activeColor = COLORS.find((c) => c.id === selectedColor) || COLORS[0];

    // Selected color ka data size ke hisaab se: { L: {...}, M: {...} }
    const dataBySize = {};
    refillData
        .filter((r) => String(r.DCLR).trim() === selectedColor)
        .forEach((r) => {
            dataBySize[String(r.DSIZE).trim()] = normalize(r);
        });

    // Sirf wo sizes jinka data API me hai, sahi order me
    const sizesToShow = Object.keys(dataBySize).sort((a, b) => sizeRank(a) - sizeRank(b));

    // Artikel number: wahi jo pehle card ke Artikel input me dikh raha hai
    // (cards aur upar ke filter/summary ki value hamesha same rahegi)
    const articleNo = dataBySize[sizesToShow[0]]?.Artikel ?? "";

    // ---- Open / close ----

    const allOpen = sizesToShow.length > 0 && sizesToShow.every((s) => openSizes[s]);

    const toggleAll = () =>
        setOpenSizes(Object.fromEntries(sizesToShow.map((s) => [s, !allOpen])));

    const toggleOne = (size) =>
        setOpenSizes((prev) => ({ ...prev, [size]: !prev[size] }));

    // ---- Render ----

    if (!isAllowed) {
        return <Navigate to="/customer-summary" replace />;
    }

    return (
        <div className="refill_main_outer">
            <Header />

            <div className="refill_main">
                {/* 1. Top filters + Back */}
                <div className="refill_header">
                    <Row style={{ width: "90%", paddingBottom: ".3em" }}>
                        <HeaderField id="refillKunden" label="Kunden" value={selectedRow?.soldTo || ""} disabled />
                        <HeaderField id="refillVers" label="Vers" value={selectedRow?.shipTo || ""} disabled />
                        <HeaderField id="refillDiv" label="Div" value={selectedRow?.div || ""} disabled />
                        <HeaderField id="refillArtikel" label="Artikel" value={String(articleNo)}/>
                        <HeaderField id="refillFarbe" label="Farbe" />
                    </Row>

                    <Row style={{ width: "11.6%", display: "flex", justifyContent: "flex-end", alignItems: "end" }}>
                        <button
                            type="button"
                            className="btn btn-secondary customerSummaryBtn"
                            style={{ width: "60px" }}
                            onClick={() => navigate("/customer-summary", { state: { row: selectedRow } })}
                        >
                            Back
                        </button>
                    </Row>
                </div>

                {/* 2. Choose a color */}
                <div className="refill_stockDetail color_section">
                    <p className="form-label-clr">Choose a color</p>
                    <div className="color_chip_row">
                        {COLORS.map((c) => (
                            <ColorChip
                                key={c.id}
                                color={c}
                                active={selectedColor === c.id}
                                onSelect={() => setSelectedColor(c.id)}
                            />
                        ))}
                    </div>
                </div>

                {/* 3. Selected color summary */}
                <div className="refill_stockDetail_color_section">
                    <div className="refill_stockDetail_color_section_child1">
                        <div className="refill_stockDtail_section1">{articleNo}</div>

                        <div className="refill_stockDtail_section2">
                            <span className="color_summary_dot" style={{ backgroundColor: activeColor.dot }}></span>
                            {activeColor.name}
                        </div>

                        <div className="refill_stockDtail_section3">
                            {activeColor.name}: {activeColor.autoOrder} pcs auto-order
                        </div>

                        {activeColor.soldOut > 0 && (
                            <div className="refill_stockDtail_section5">
                                {activeColor.soldOut} size(s) sold out
                            </div>
                        )}
                    </div>

                    <div className="refill_stockDetail_color_section_child2">
                        <div className="refill_stockDtail_section4">
                            Valid {activeColor.validFrom} – {activeColor.validTo}
                        </div>
                    </div>
                </div>

                {/* 4. Open / close all */}
                <div className="refill_stockDetail_table_main">
                    <button
                        type="button"
                        className="btn-text theme-inverse show_filed"
                        onClick={toggleAll}
                        aria-expanded={allOpen}
                        aria-label={allOpen ? "Hide details for all sizes" : "Show details for all sizes"}
                    >
                        {allOpen ? "−" : "+"}
                    </button>
                </div>

                {/* 5. Size cards */}
                {loading && <Loader />}
                {error && <p className="text-danger refill_message">{error}</p>}

                <div className="refill_stockDetail_tableData_main">
                    {!loading && sizesToShow.length === 0 && (
                        <p className="refill_empty">No data available for this color</p>
                    )}

                    {sizesToShow.map((size) => (
                        <SizeCard
                            key={`${selectedColor}-${size}`}
                            size={size}
                            progress={PROGRESS}
                            isOpen={!!openSizes[size]}
                            onToggle={() => toggleOne(size)}
                            extraFields={EXTRA_FIELDS}
                            data={dataBySize[size]}
                        />
                    ))}
                </div>
            </div>

            <Footer className="footer" />
        </div>
    );
};

export default Refill;